/**
 * Shared icon set for the FloatingChat brand-standard rebrand
 * (track:cc-rebrand-pr3-chrome). Replaces the Unicode-glyph status
 * indicators used across the tabs (✅/✓, ⚠️/⚠, ✗, ○, →, ↑/↓, plus
 * QualityPipeline's own ↻/⋮/◉ — added in track:cc-rebrand-pr5-qualitypipeline
 * once that file's actual render-time glyph inventory turned out wider
 * than the original directory-level grep found) with a small,
 * hand-drawn-style SVG set matching FloatingChat's icon language
 * (FloatingChat.tsx — starburst logo, plus/close/checkmark-circle/
 * help-circle glyphs; no icon library, inline SVG throughout).
 *
 * Every icon uses `currentColor` for its stroke/fill — never a hardcoded
 * hex value — so it always inherits a token-driven, theme-correct color
 * from its container (set color via className, e.g. `text-[var(--green)]`).
 * This is the dark-mode-safety requirement for the whole branding pass:
 * FloatingChat's own icons are hardcoded MC-hex and have no dark-mode
 * story, but CC v2's must, since CC v2 is themed and FloatingChat isn't.
 *
 * Consumed per-tab starting in PR 4 (LogSearchTab.tsx, which turned out to
 * need none of these) and PR 5 (QualityPipeline.tsx).
 */
import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function baseProps({ size = 14, ...rest }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 16 16",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    ...rest,
  };
}

/** Done / success — replaces ✅ / ✓ */
export function CheckIcon(props: IconProps) {
  return (
    <svg {...baseProps(props)}>
      <path d="M3 8.5L6.5 12L13 4" />
    </svg>
  );
}

/** Warning — replaces ⚠️ / ⚠ */
export function WarningIcon(props: IconProps) {
  return (
    <svg {...baseProps(props)}>
      <path d="M8 2L14.5 13.5H1.5L8 2Z" strokeLinejoin="round" />
      <path d="M8 6.5V9.5" />
      <circle cx="8" cy="11.75" r="0.75" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Closed / error — replaces ✗ */
export function CloseIcon(props: IconProps) {
  return (
    <svg {...baseProps(props)}>
      <path d="M3.5 3.5L12.5 12.5" />
      <path d="M12.5 3.5L3.5 12.5" />
    </svg>
  );
}

/** Pending / empty — replaces ○ */
export function PendingIcon(props: IconProps) {
  return (
    <svg {...baseProps(props)}>
      <circle cx="8" cy="8" r="5.5" />
    </svg>
  );
}

/** No-op / not applicable — replaces ⊘ */
export function NoopIcon(props: IconProps) {
  return (
    <svg {...baseProps(props)}>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M4.1 4.1L11.9 11.9" />
    </svg>
  );
}

/** Start / mark in progress — replaces ▶ */
export function PlayIcon(props: IconProps) {
  return (
    <svg {...baseProps(props)}>
      <path d="M4.5 3.5L12.5 8L4.5 12.5V3.5Z" fill="currentColor" stroke="none" strokeLinejoin="round" />
    </svg>
  );
}

/** Drag handle — replaces ⋮⋮ */
export function GripIcon(props: IconProps) {
  return (
    <svg {...baseProps(props)}>
      <circle cx="5.5" cy="4" r="1" fill="currentColor" stroke="none" />
      <circle cx="10.5" cy="4" r="1" fill="currentColor" stroke="none" />
      <circle cx="5.5" cy="8" r="1" fill="currentColor" stroke="none" />
      <circle cx="10.5" cy="8" r="1" fill="currentColor" stroke="none" />
      <circle cx="5.5" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="10.5" cy="12" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Active / in-progress (filled) — replaces ◉, and the busy-state ⋮ */
export function ActiveIcon(props: IconProps) {
  return (
    <svg {...baseProps(props)}>
      <circle cx="8" cy="8" r="5.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Resuming / retry — replaces ↻ */
export function RefreshIcon(props: IconProps) {
  return (
    <svg {...baseProps(props)}>
      <path d="M12.5 8A4.5 4.5 0 1 1 11 4.7" />
      <path d="M12.5 3.5V7H9" />
    </svg>
  );
}

/** Editing / draft — Build-A-Spec nav + modal header */
export function PencilIcon(props: IconProps) {
  return (
    <svg {...baseProps(props)}>
      <path d="M10.5 2.5L13.5 5.5L5 14H2V11L10.5 2.5Z" strokeLinejoin="round" />
      <path d="M8.5 4.5L11.5 7.5" />
    </svg>
  );
}

/** Action-suffix / sort direction — replaces →, ↑, ↓ via `direction` */
export function ArrowIcon({ direction = "right", ...props }: IconProps & { direction?: "right" | "up" | "down" }) {
  const rotation = direction === "up" ? -90 : direction === "down" ? 90 : 0;
  return (
    <svg {...baseProps(props)} style={{ transform: `rotate(${rotation}deg)`, ...props.style }}>
      <path d="M3 8H13" />
      <path d="M9 4L13 8L9 12" />
    </svg>
  );
}
